import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import { Search, Clear, CheckCircleOutline, BlockOutlined } from "@mui/icons-material";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  getHighlights,
  updateHighlightStatus,
} from "service/admin/contentAdminService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const statusChip = (status) => {
  const map = {
    active: { label: "Active", color: "success" },
    inactive: { label: "Inactive", color: "default" },
  };
  const cfg = map[status] || { label: status || "-", color: "default" };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const Highlights = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      };
      const response = await getHighlights(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load highlights.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await updateHighlightStatus(id, newStatus);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
      );
      toast.success(
        `Highlight ${newStatus === "active" ? "activated" : "deactivated"}.`
      );
    } catch (error) {
      toast.error("Failed to update highlight status.");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) =>
        page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "athlete_name",
      headerName: "ATHLETE NAME",
      flex: 1.2,
      renderCell: (params) =>
        params.row.athlete?.full_name || params.value || "-",
    },
    {
      field: "username",
      headerName: "USERNAME",
      flex: 1,
      renderCell: (params) =>
        params.row.athlete?.username || params.value || "-",
    },
    {
      field: "title_heading",
      headerName: "TITLE / HEADING",
      flex: 1.2,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "status",
      headerName: "STATUS",
      flex: 0.8,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "created_at",
      headerName: "CREATED AT",
      flex: 1,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY HH:mm") : "-",
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.9,
      sortable: false,
      renderCell: (params) => {
        const isActive = params.row.status === "active";
        return (
          <Button
            size="small"
            variant="outlined"
            color={isActive ? "error" : "success"}
            startIcon={
              isActive ? (
                <BlockOutlined fontSize="small" />
              ) : (
                <CheckCircleOutline fontSize="small" />
              )
            }
            onClick={() =>
              handleToggleStatus(params.row.id, params.row.status)
            }
            sx={{ fontSize: "0.7rem", px: 1 }}
          >
            {isActive ? "Deactivate" : "Activate"}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header title="HIGHLIGHTS" subTitle="Manage athlete highlights" />
        </FlexBetween>

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search by name or username..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 280 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel color="secondary">Status</InputLabel>
            <Select
              color="secondary"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          mt="10px"
          height="65vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default Highlights;
